import type {
  CommunityConfiguration,
  CommunityConfigurationAction,
  CommunityConfigurationCommunity,
  CommunityConfigurationLocalTaxes,
  CommunityConfigurationOtherLocalTax,
  CommunityConfigurationOwner,
  CommunityConfigurationPolicyNumber,
  CommunityConfigurationRentManagerSettings,
  CommunityConfigurationState,
  SaveCommunityConfigurationInput,
} from "@graphql/api";
import { PortfolioIntegrationType } from "@graphql/api";
import { states } from "@covertree/consts/states/states";
import { format, isValid, parseISO } from "date-fns";

export type NumericFormValue = number | "";

export type CommunityConfigurationFormCommunity = Omit<
  CommunityConfigurationCommunity,
  "__typename" | "ctmhcId" | "defaultAddendumDate" | "ownerClientId"
> & {
  ctmhcId: string;
  /**
   * Addendum date given to the residents imported by the initial sync when the
   * configuration is activated (Rent Manager portfolios only). `null` when not
   * configured.
   */
  defaultAddendumDate: Date | null;
  ownerClientId: string;
};

/** Parses an `AWSDate` (`YYYY-MM-DD`) into a local date for the date picker. */
export const parseAWSDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

/** Formats a date picker value as an `AWSDate` (`YYYY-MM-DD`). */
export const formatAWSDate = (value: Date | null): string | null =>
  value && isValid(value) ? format(value, "yyyy-MM-dd") : null;

export type CommunityConfigurationFormOwner = Omit<
  CommunityConfigurationOwner,
  | "__typename"
  | "addressLine1"
  | "addressLine2"
  | "city"
  | "county"
  | "municipality"
  | "name"
  | "state"
  | "zipCode"
> & {
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  municipality: string;
  name: string;
  state: {
    label: string;
    value: string;
  };
  zipCode: string;
};

export type CommunityConfigurationFormOtherLocalTax = Omit<
  CommunityConfigurationOtherLocalTax,
  "__typename"
>;

export type CommunityConfigurationFormLocalTaxes = Omit<
  CommunityConfigurationLocalTaxes,
  "__typename" | "cityName" | "countyName" | "otherLocalTaxes"
> & {
  cityName: string;
  countyName: string;
  otherLocalTaxes: CommunityConfigurationFormOtherLocalTax[];
};

export type CommunityConfigurationFormPolicyNumber = Omit<
  CommunityConfigurationPolicyNumber,
  "__typename" | "policyNumber"
> & {
  policyNumber: string;
};

export type CommunityConfigurationFormState = Omit<
  CommunityConfigurationState,
  | "__typename"
  | "localTaxes"
  | "policyNumbers"
  | "stampingFee"
  | "stateTax"
  | "surplusLinesTax"
> & {
  localTaxes: CommunityConfigurationFormLocalTaxes[];
  policyNumbers: CommunityConfigurationFormPolicyNumber[];
  stampingFee: NumericFormValue;
  stateTax: NumericFormValue;
  surplusLinesTax: NumericFormValue;
};

/**
 * Searchable-autocomplete option for a Rent Manager General Ledger account.
 * `value` is `""` until the operator picks an account; `label` is resolved
 * from the loaded accounts, so it may be blank for a stored id.
 */
export interface CommunityConfigurationFormGlAccount {
  label: string;
  value: NumericFormValue;
}

/**
 * Rent Manager location with the General Ledger account the BRL charge type is
 * booked against.
 */
export interface CommunityConfigurationFormRentManagerLocation {
  glAccount: CommunityConfigurationFormGlAccount;
  id: number;
  name: string;
}

/**
 * Rent Manager write-back settings: whether resident transitions are posted as
 * live History Notes and whether default UDF values are written on sync.
 */
export type CommunityConfigurationFormRentManagerSettings = Omit<
  CommunityConfigurationRentManagerSettings,
  "__typename"
>;

export interface CommunityConfigurationFormValues {
  communities: CommunityConfigurationFormCommunity[];
  owners: CommunityConfigurationFormOwner[];
  policyDefaults: {
    limits: {
      animalLiabilityLimit: NumericFormValue;
      blanketRentalResidentLiabilityLimit: NumericFormValue;
      medicalPaymentsLimit: NumericFormValue;
      premisesLiabilityLimit: NumericFormValue;
    };
    mgaFee: NumericFormValue;
    operatorFee: NumericFormValue;
    pricePerUnit: NumericFormValue;
  };
  /** Only populated for Rent Manager integrated portfolios. */
  rentManagerLocations: CommunityConfigurationFormRentManagerLocation[];
  /** `null` for portfolios not integrated with Rent Manager. */
  rentManagerSettings: CommunityConfigurationFormRentManagerSettings | null;
  states: CommunityConfigurationFormState[];
}

const valueOrBlank = (value?: number | null): NumericFormValue => value ?? "";

export const makeCommunityConfigurationClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `owner-${Date.now()}-${Math.random()}`;

export const parseNumericFormValue = (value: string): NumericFormValue =>
  value === "" ? "" : Number(value);

export function validateNonNegativeNumber(
  value: unknown,
  label: string,
  required = false
): true | string {
  if (value === "" || value === null || value === undefined) {
    return required ? `${label} is required` : true;
  }

  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? true
    : `${label} must be a non-negative number`;
}

export function toStateOption(
  state: string | null | undefined
): CommunityConfigurationFormOwner["state"] {
  return {
    label: states.find(({ value }) => value === state)?.label ?? "",
    value: state ?? "",
  };
}

export function createCommunityConfigurationFormValues(
  configuration: CommunityConfiguration
): CommunityConfigurationFormValues {
  return {
    communities: configuration.communities.map((community) => ({
      ...community,
      ctmhcId: community.ctmhcId ?? "",
      defaultAddendumDate: parseAWSDate(community.defaultAddendumDate),
      ownerClientId: community.ownerClientId ?? "",
    })),
    owners: configuration.owners.map((owner) => ({
      ...owner,
      addressLine1: owner.addressLine1 ?? "",
      addressLine2: owner.addressLine2 ?? "",
      city: owner.city ?? "",
      county: owner.county ?? "",
      municipality: owner.municipality ?? "",
      name: owner.name ?? "",
      state: toStateOption(owner.state),
      zipCode: owner.zipCode ?? "",
    })),
    policyDefaults: {
      limits: {
        animalLiabilityLimit: valueOrBlank(
          configuration.policyDefaults?.limits.animalLiabilityLimit
        ),
        blanketRentalResidentLiabilityLimit: valueOrBlank(
          configuration.policyDefaults?.limits
            .blanketRentalResidentLiabilityLimit
        ),
        medicalPaymentsLimit: valueOrBlank(
          configuration.policyDefaults?.limits.medicalPaymentsLimit
        ),
        premisesLiabilityLimit: valueOrBlank(
          configuration.policyDefaults?.limits.premisesLiabilityLimit
        ),
      },
      mgaFee: valueOrBlank(configuration.policyDefaults?.mgaFee),
      operatorFee: valueOrBlank(configuration.policyDefaults?.operatorFee),
      pricePerUnit: valueOrBlank(configuration.policyDefaults?.pricePerUnit),
    },
    rentManagerLocations: (
      configuration.portfolio.rentManagerLocations ?? []
    ).map(({ glAccountId, id, name }) => ({
      glAccount: { label: "", value: valueOrBlank(glAccountId) },
      id,
      name,
    })),
    rentManagerSettings:
      configuration.portfolio.integrationType ===
      PortfolioIntegrationType.RentManager
        ? {
            liveNotesEnabled:
              configuration.portfolio.rentManagerSettings?.liveNotesEnabled ??
              false,
            shouldWriteDefaultUDFValues:
              configuration.portfolio.rentManagerSettings
                ?.shouldWriteDefaultUDFValues ?? false,
          }
        : null,
    states: configuration.states.map((state) => ({
      ...state,
      localTaxes: state.localTaxes.map((tax) => ({
        ...tax,
        cityName: tax.cityName ?? "",
        cityTaxRate: tax.cityTaxRate ?? null,
        countyName: tax.countyName ?? "",
        countyTaxRate: tax.countyTaxRate ?? null,
        otherLocalTaxes: [...tax.otherLocalTaxes],
      })),
      policyNumbers: state.policyNumbers.map((policyNumber) => ({
        ...policyNumber,
        policyNumber: policyNumber.policyNumber ?? "",
      })),
      stampingFee: valueOrBlank(state.stampingFee),
      stateTax: valueOrBlank(state.stateTax),
      surplusLinesTax: valueOrBlank(state.surplusLinesTax),
    })),
  };
}

export function validateCommunityConfigurationForActivation(
  values: CommunityConfigurationFormValues
): string[] {
  const errors: string[] = [];
  const ownerById = new Map(
    values.owners.map((owner) => [owner.clientId, owner])
  );
  const requiredPolicyValues: Array<[unknown, string]> = [
    [values.policyDefaults.pricePerUnit, "Price per unit"],
    [values.policyDefaults.operatorFee, "Operator fee"],
    [values.policyDefaults.mgaFee, "MGA fee"],
    [
      values.policyDefaults.limits.blanketRentalResidentLiabilityLimit,
      "Blanket rental resident liability limit",
    ],
  ];
  if (
    requiredPolicyValues.some(
      ([value, label]) => validateNonNegativeNumber(value, label, true) !== true
    )
  ) {
    errors.push("Complete all required portfolio policy values.");
  }

  for (const location of values.rentManagerLocations) {
    if (location.glAccount.value === "") {
      errors.push(
        `Rent Manager location ${location.name} requires a General Ledger account for the BRL charge type.`
      );
    }
  }

  const optionalLimits: Array<[unknown, string]> = [
    [
      values.policyDefaults.limits.animalLiabilityLimit,
      "Animal liability limit",
    ],
    [
      values.policyDefaults.limits.medicalPaymentsLimit,
      "Medical payments limit",
    ],
    [
      values.policyDefaults.limits.premisesLiabilityLimit,
      "Premises liability limit",
    ],
  ];
  if (
    optionalLimits.some(
      ([value, label]) => validateNonNegativeNumber(value, label) !== true
    )
  ) {
    errors.push("Policy limits must be non-negative numbers.");
  }

  for (const community of values.communities.filter(
    ({ isActive }) => isActive
  )) {
    if (!community.ctmhcId?.trim()) {
      errors.push(`${community.name} requires a CTMHCID.`);
    }
    if (!community.ownerClientId) {
      errors.push(`${community.name} requires a legal entity owner.`);
      continue;
    }
    const owner = ownerById.get(community.ownerClientId);
    if (
      !owner ||
      !owner.name.trim() ||
      !owner.addressLine1.trim() ||
      !owner.city.trim() ||
      !owner.county.trim() ||
      !owner.state?.value.trim() ||
      !owner.zipCode.trim()
    ) {
      errors.push(`${community.name}'s legal entity owner is incomplete.`);
    }
    const state = values.states.find(({ state }) => state === community.state);
    const policyNumber = state?.policyNumbers.find(
      ({ ownerClientId }) => ownerClientId === community.ownerClientId
    )?.policyNumber;
    if (!policyNumber?.trim()) {
      errors.push(
        `${community.state} requires a policy number for ${owner?.name || "the selected owner"}.`
      );
    }
  }

  const activeCtmhcIds = values.communities
    .filter(({ isActive, ctmhcId }) => isActive && ctmhcId?.trim())
    .map(({ ctmhcId }) => ctmhcId!.trim());
  if (new Set(activeCtmhcIds).size !== activeCtmhcIds.length) {
    errors.push("Active community CTMHCIDs must be unique.");
  }

  for (const state of values.states) {
    const stateRates: Array<[unknown, string]> = [
      [state.surplusLinesTax, "Surplus lines tax rate"],
      [state.stampingFee, "Stamping fee rate"],
      [state.stateTax, "State tax rate"],
    ];
    if (
      stateRates.some(
        ([value, label]) => validateNonNegativeNumber(value, label) !== true
      )
    ) {
      errors.push(`${state.state} tax rates must be non-negative numbers.`);
    }

    for (const localTax of state.localTaxes) {
      if (
        localTax.cityTaxRate !== null &&
        localTax.cityTaxRate !== undefined &&
        !localTax.cityName?.trim()
      ) {
        errors.push(`${state.state} city tax rates require a city name.`);
      }
      if (
        localTax.countyTaxRate !== null &&
        localTax.countyTaxRate !== undefined &&
        !localTax.countyName?.trim()
      ) {
        errors.push(`${state.state} county tax rates require a county name.`);
      }
      if (
        validateNonNegativeNumber(localTax.cityTaxRate, "City tax rate") !==
          true ||
        validateNonNegativeNumber(localTax.countyTaxRate, "County tax rate") !==
          true ||
        localTax.otherLocalTaxes.some(
          ({ name, rate }) =>
            !name.trim() ||
            validateNonNegativeNumber(rate, "Local tax rate", true) !== true
        )
      ) {
        errors.push(`${state.state} local taxes are incomplete or invalid.`);
      }
    }
  }

  return Array.from(new Set(errors));
}

const numberOrUndefined = (value: NumericFormValue) =>
  value === "" ? undefined : Number(value);

const numberOrNull = (value: NumericFormValue) =>
  value === "" ? null : Number(value);

export function toSaveCommunityConfigurationInput(
  portfolioId: string,
  action: CommunityConfigurationAction,
  values: CommunityConfigurationFormValues
): SaveCommunityConfigurationInput {
  return {
    action,
    communities: values.communities.map(
      ({
        ctmhcId,
        defaultAddendumDate,
        isActive,
        ownerClientId,
        propertyId,
      }) =>
        values.rentManagerSettings
          ? {
              ctmhcId: ctmhcId || null,
              defaultAddendumDate: formatAWSDate(defaultAddendumDate),
              isActive,
              ownerClientId: ownerClientId || null,
              propertyId,
            }
          : {
              ctmhcId: ctmhcId || null,
              isActive,
              ownerClientId: ownerClientId || null,
              propertyId,
            }
    ),
    owners: values.owners.map((owner) => ({
      ...owner,
      id: owner.id || null,
      state: owner.state?.value ?? "",
    })),
    policyDefaults: {
      limits: {
        animalLiabilityLimit: numberOrUndefined(
          values.policyDefaults.limits.animalLiabilityLimit
        ),
        blanketRentalResidentLiabilityLimit: numberOrUndefined(
          values.policyDefaults.limits.blanketRentalResidentLiabilityLimit
        ),
        medicalPaymentsLimit: numberOrUndefined(
          values.policyDefaults.limits.medicalPaymentsLimit
        ),
        premisesLiabilityLimit: numberOrUndefined(
          values.policyDefaults.limits.premisesLiabilityLimit
        ),
      },
      mgaFee: numberOrUndefined(values.policyDefaults.mgaFee),
      operatorFee: numberOrUndefined(values.policyDefaults.operatorFee),
      pricePerUnit: numberOrUndefined(values.policyDefaults.pricePerUnit),
    },
    portfolioId,
    rentManagerLocations: values.rentManagerLocations.length
      ? values.rentManagerLocations.map(({ glAccount, id }) => ({
          glAccountId: numberOrNull(glAccount.value),
          locationId: id,
        }))
      : undefined,
    rentManagerSettings: values.rentManagerSettings
      ? {
          liveNotesEnabled: values.rentManagerSettings.liveNotesEnabled,
          shouldWriteDefaultUDFValues:
            values.rentManagerSettings.shouldWriteDefaultUDFValues,
        }
      : undefined,
    states: values.states.map((state) => ({
      ...state,
      localTaxes: state.localTaxes.map((tax) => ({
        ...tax,
        cityTaxRate: tax.cityTaxRate ?? undefined,
        countyTaxRate: tax.countyTaxRate ?? undefined,
      })),
      stampingFee: numberOrNull(state.stampingFee),
      stateTax: numberOrNull(state.stateTax),
      surplusLinesTax: numberOrNull(state.surplusLinesTax),
    })),
  };
}
